import { Configuration, LogLevel } from '@azure/msal-browser';
import { getConfigSync } from '../helpers/configHelper';

// Función para crear configuración MSAL desde config.json
const createMsalConfig = (): Configuration => {
  const config = getConfigSync();
  
  if (!config) {
    throw new Error('Configuración no cargada. Asegúrate de llamar getConfig() primero.');
  }

  return {
    auth: {
      clientId: config.AZURE_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${config.AZURE_TENANT_ID}`,
      redirectUri: config.AZURE_REDIRECT_URI,
      postLogoutRedirectUri: config.AZURE_POST_LOGOUT_REDIRECT_URI,
      navigateToLoginRequestUrl: true,
      clientCapabilities: ['CP1'] // Capacidades del cliente para SSO
    },
    cache: {
      cacheLocation: 'localStorage', // Almacenamiento en localStorage
      storeAuthStateInCookie: false // Usar cookies solo para IE11
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          if (containsPii) {
            return;
          }
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
          }
        },
        piiLoggingEnabled: false // No mostrar información personal en logs
      }
    }
  };
};

// Variable para almacenar la configuración MSAL una vez creada
let msalConfigInstance: Configuration | null = null;

// Función para obtener la configuración MSAL
export const getMsalConfig = (): Configuration => {
  if (!msalConfigInstance) {
    msalConfigInstance = createMsalConfig();
  }
  return msalConfigInstance;
};

// Función para crear configuración básica desde config.json
const createAzureConfig = () => {
  const config = getConfigSync();
  
  if (!config) {
    throw new Error('Configuración no cargada. Asegúrate de llamar getConfig() primero.');
  }

  return {
    clientId: config.AZURE_CLIENT_ID,
    tenantId: config.AZURE_TENANT_ID,
    authority: `https://login.microsoftonline.com/${config.AZURE_TENANT_ID}`,
    redirectUri: config.AZURE_REDIRECT_URI,
    postLogoutRedirectUri: config.AZURE_POST_LOGOUT_REDIRECT_URI,
  };
};

// Función para obtener scopes desde config.json
const getScopes = (): string[] => {
  const config = getConfigSync();
  
  if (!config) {
    return ['User.Read', 'profile', 'email', 'openid'];
  }

  return config.AZURE_SCOPES.split(',').map(scope => scope.trim());
};

// Funciones para obtener configuración de manera diferida
export const getMsalConfigSafe = (): Configuration => {
  try {
    return getMsalConfig();
  } catch (error) {
    console.error('Error obteniendo configuración MSAL:', error);
    throw error;
  }
};

export const getAzureConfig = () => {
  try {
    return createAzureConfig();
  } catch (error) {
    console.error('Error obteniendo configuración Azure:', error);
    throw error;
  }
};



// Función para obtener loginRequest de manera diferida
export const getLoginRequest = () => {
  return {
    scopes: getScopes()
  };
};

// Función para obtener silentRequest de manera diferida
export const getSilentRequest = () => {
  return {
    scopes: getScopes()
  };
};

// Exportaciones para compatibilidad (solo funciones, no valores inmediatos)
export { getMsalConfig as msalConfig };
export const azureConfig = getAzureConfig;
